import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authExchangeSession } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { setAdminToken, clearAdminToken } from "../lib/admin";
import { toast } from "sonner";

/**
 * Handles the redirect back from Emergent Google Auth.
 * The provider lands the user at `<origin><returnPath>#session_id=...`.
 * We pull the session_id out of the URL fragment, exchange it for our app
 * session (httpOnly cookie), strip the hash, then navigate to the intended
 * destination.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    // Where to go after we finish — encoded in pathname.
    const target = window.location.pathname || "/me/trips";

    if (!sessionId) {
      navigate(target, { replace: true });
      return;
    }

    (async () => {
      try {
        const data = await authExchangeSession(sessionId);
        setUser(data.user);
        // If this user is in ADMIN_EMAILS, the backend also mints an admin JWT
        // so they can use the existing /admin dashboard + hamburger Super Admin section.
        if (data.admin_token) {
          setAdminToken(data.admin_token);
        } else {
          clearAdminToken();
        }
        toast.success(`Welcome${data.user?.name ? `, ${data.user.name.split(" ")[0]}` : ""} ✦`);
      } catch (e) {
        toast.error("Sign-in failed. Please try again.");
      } finally {
        // Strip the hash so the user-facing URL is clean
        window.history.replaceState(null, "", target);
        navigate(target, { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAF7F0]">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto rounded-full border-2 border-[#2A7FB8] border-t-transparent animate-spin" />
        <p className="font-mono text-xs mt-4 text-slate-500 tracking-widest">SIGNING YOU IN…</p>
      </div>
    </div>
  );
}
